/* ============================================================================
 * header-search-toggle.js
 *
 * Vanilla replacement for the header search drawer toggle. Replaces the
 * jQuery Search component that lives in app.js and the dead theme.SearchDrawer
 * in theme.js (the latter's selectors didn't match any markup any more).
 *
 * Behavior:
 *
 *   The header has a search icon button [data-header-search-toggle] that
 *   targets a <predictive-search> drawer by aria-controls (default
 *   #SearchDrawer). Clicking it slides the drawer down from the top of the
 *   page; the inside <input data-header-search-input> gets focus.
 *
 *   Closing happens via the in-drawer close button [data-header-search-close],
 *   pressing Escape, or clicking the toggle again. Body scroll is locked
 *   while the drawer is open.
 *
 * Animation:
 *
 *   Lives in component-header-search.css.liquid — scaleY 0 → 1 with
 *   transform-origin: top, and a translateY + opacity fade on the inner
 *   content. Matches the look of the legacy anime.js animation that ran in
 *   app.js's Search component.
 *
 * Attribute namespace:
 *
 *   We use data-header-search-* (not data-search-*) so app.js's Search
 *   component can't bind to the same elements. App.js still loads but its
 *   Search.init() runs against an empty selector set and no-ops.
 * ========================================================================= */

(function () {
  'use strict';

  var OPEN_CLASS = 'header-search-open';

  var openDrawer = null;
  var lastTrigger = null;
  var prevBodyOverflow = '';

  function openSearch(trigger) {
    var id = trigger.getAttribute('aria-controls');
    if (!id) return;
    var drawer = document.getElementById(id);
    if (!drawer) return;
    if (drawer.classList.contains(OPEN_CLASS)) return;

    lastTrigger = trigger;
    drawer.classList.add(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'true');

    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    openDrawer = drawer;

    document.addEventListener('keydown', onKeydown);

    // Focus the search input on the next frame so the open transition can
    // start before focus jumps.
    var input = drawer.querySelector('[data-header-search-input]');
    if (input) {
      requestAnimationFrame(function () {
        try { input.focus(); } catch (e) { /* ignore */ }
      });
    }

    drawer.dispatchEvent(new CustomEvent('header-search:open', { bubbles: true }));
  }

  function closeSearch() {
    if (!openDrawer) return;
    var drawer = openDrawer;

    drawer.classList.remove(OPEN_CLASS);

    if (lastTrigger) {
      lastTrigger.setAttribute('aria-expanded', 'false');
      try { lastTrigger.focus(); } catch (e) { /* ignore */ }
    }

    document.body.style.overflow = prevBodyOverflow || '';
    document.removeEventListener('keydown', onKeydown);

    openDrawer = null;
    lastTrigger = null;

    drawer.dispatchEvent(new CustomEvent('header-search:close', { bubbles: true }));
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeSearch();
    }
  }

  /* Toggle button — open if closed, close if already open (matches the
   * legacy app.js Search component behavior). */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-header-search-toggle]');
    if (trigger) {
      e.preventDefault();
      var id = trigger.getAttribute('aria-controls');
      if (id && openDrawer && openDrawer.id === id) {
        closeSearch();
      } else {
        openSearch(trigger);
      }
      return;
    }

    var closeBtn = e.target.closest('[data-header-search-close]');
    if (closeBtn) {
      e.preventDefault();
      closeSearch();
    }
  });
})();
